import React from 'react';
import { Stack } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import Select from 'components/select';
import { environmentDetails, isDataDiskConfigFullfilled, isSkuConfigFullfilled, sspSubmitOrder } from 'pages/provisioning/store/day2-operation';
import ProvisioningStyles from 'pages/provisioning/day2-operation/style';
import HelpImg from 'pages/provisioning/_components/helpImg';

const filter = createFilterOptions();
const Application = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [envDetails, setEnvDetails] = useRecoilState(environmentDetails);
    const [, setSkuConfig] = useRecoilState(isSkuConfigFullfilled);
    const [, setDDConfig] = useRecoilState(isDataDiskConfigFullfilled);
    const [value, setValue] = React.useState(order?.appProfileDetails || null);
    const [ccInputValue, setCCInputValue] = React.useState(order?.appProfile || '');
    const [options, setOptions] = React.useState([]);
    const [initOptions, setInitOptions] = React.useState([]);

    const appProfileDetailsApi = useMutation((param) => axios.get(`${endPoints.ssp.vm.appProfileDetails}/${param?.id}`));

    const appGetApi = useMutation(() =>
        axios.post(`${endPoints.ssp.vm.appProfiles}`, {
            cloudProvider: order?.cloudProvider,
            cloudService: order?.cloudService,
            name: ccInputValue,
        })
    );

    const appPostApi = useMutation((payload) => axios.post(`${endPoints.ssp.vm.appProfiles}`, payload));

    const setAppService = (appProfile, appData) => {
        setOrder({
            ...order,
            appProfile: appProfile?.label,
            appProfileDetails: appProfile,
            entityDetails: appData,
            billingEntity: appData?.billingEntity || null,
            hCode: appData?.financialDetails || null,
            ...(appData?.financialDetails && { hCodeDetails: { label: appData?.financialDetails, projectName: appData?.projectName } }),
            ...(!appData?.financialDetails && { hCodeDetails: null }),
            environment: null,
            region: null,
            regionDisplayName: null,
            landingZone: null,
            landingZoneDetails: null,
            instanceName: '',
            fqdns: '',
            instanceFrontendPoolName: '',
            availabilityZone: null,
            bpPoolName: '',
            virtualMachine: [],
            vNet: environmentDetails?.virtualNetwork,
            rulesName: '',
            protocols: null,
            frontendPort: '',
            backendPort: '',
            sessionPersistance: null,
            timeOut: 15,
            healthProbeName: '',
            hpProtocol: null,
            port: '',
            urlPath: '',
            appTier: null,
            appTierCode: null,
            hoursOfOperation: null,
            envDetails: undefined,
            tags: [],
            defaultTags: [],
            validationStatus: 'forceValidate',
            poolConfigurations: 'nic',
            isTouched: false,
        });
        setEnvDetails({
            ...envDetails,
            ...(appData?.financialDetails && { Project: appData?.projectName }),
        });
        setSkuConfig(false);
        setDDConfig(false);
    };

    const resetOptions = () => {
        setOptions(initOptions);
    };

    const fetch = React.useMemo(
        () =>
            throttle(() => {
                appGetApi.mutate();
            }, 1000),
        []
    );

    React.useEffect(() => {
        if (ccInputValue === '') {
            resetOptions();
        }
        if (ccInputValue && ccInputValue !== '' && ccInputValue?.length > 2) fetch();
    }, [ccInputValue, fetch]);

    React.useEffect(() => {
        if (appGetApi?.isSuccess) {
            if (appGetApi?.data?.data) {
                const combinedValue = appGetApi?.data?.data.map((app) => ({ label: `${app.name}`, value: `${app.name}`, data: app }));
                setOptions(combinedValue);
            }
        }
    }, [appGetApi?.isSuccess]);

    React.useEffect(() => {
        if (order?.cloudProvider && !order?.appProfile) {
            appPostApi.mutate({
                cloudProvider: order?.cloudProvider,
                cloudService: order?.cloudService,
                name: '',
            });
        }
    }, [order?.cloudProvider, order?.appProfile]);

    React.useEffect(() => {
        if (appPostApi?.isSuccess) {
            if (appPostApi?.data?.data) {
                const combinedValue = appPostApi?.data?.data.map((app) => ({ label: `${app.name}`, value: `${app.name}`, data: app }));
                setOptions(combinedValue);
                setInitOptions(combinedValue);
            }
        }
    }, [appPostApi?.isSuccess]);

    React.useEffect(() => {
        if (value === null && initOptions?.length) {
            resetOptions();
        }
        if (value !== null && value?.data?.id) {
            setAppService(value, null);
            appProfileDetailsApi.mutate({
                id: value?.data?.id,
            });
        }
    }, [value]);

    React.useEffect(() => {
        if (appProfileDetailsApi?.isSuccess) {
            setAppService({ label: appProfileDetailsApi?.data?.data?.name, value: appProfileDetailsApi?.data?.data?.name }, appProfileDetailsApi?.data?.data);
        }
    }, [appProfileDetailsApi?.isSuccess]);

    React.useEffect(() => {
        if (!order?.appProfile && order?.isTouched) setIsTouched(true);
    }, [order?.appProfile, order?.isTouched]);

    return (
        <>
            <Stack direction="row" spacing={1} sx={ProvisioningStyles.stack}>
                <Select
                    key="applicationName"
                    label="Application *"
                    placeholder="Type to search"
                    value={order?.appProfileDetails || null}
                    onInputChange={(event, newInputValue) => {
                        if (newInputValue && newInputValue !== '') setCCInputValue(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                            });
                        } else if (newValue && newValue.inputValue) {
                            setValue({
                                label: newValue.inputValue,
                            });
                        } else {
                            setValue(newValue);
                        }
                    }}
                    filterOptions={(optionValues, params) => filter(optionValues, params)}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    options={options}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        return option.label;
                    }}
                    renderOption={(props, option) => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <li {...props}>{option.label}</li>
                    )}
                    sx={ProvisioningStyles.costCenter}
                    freeSolo
                    isRequired={isTouched}
                />
                <HelpImg title="Type to Search Application." />
            </Stack>
        </>
    );
};

export default React.memo(Application);
